import { sectionTypes } from '@playbooks/core/src/constants'
import { ActionCard, Section, AiTeammateScope } from '@playbooks/core/src/interfaces/ActionCard'

/** Chapter ID used for sections placed before the first chapter (separator) */
const NO_CHAPTER_ID = -1

/**
 * Filter sections that should be analyzed and filled out by the AI Teammate widget,
 * based on the configured scope and respecting the "Exclude from AI analysis" setting
 * of both sections and chapters.
 */
export function getTeammateTargetSections(
  actionCard: ActionCard | undefined,
  currentSectionId: number | undefined,
  scope: AiTeammateScope,
): Section[] {
  if (!actionCard?.sections?.length) {
    return []
  }

  // Map each content section to its chapter in a single pass, collecting excluded chapters along the way.
  // The chapter is derived from the section order, same as `idOfSep` in `enrichActionCardWithProps`.
  const chapterIdBySectionId = new Map<number, number>()
  const excludedChapterIds = new Set<number>()
  const contentSections: Section[] = []
  let chapterId = NO_CHAPTER_ID
  for (const sec of actionCard.sections) {
    if (sec.type === sectionTypes.SEPARATOR) {
      chapterId = sec.id
      if (sec.isExcludedFromAi) excludedChapterIds.add(sec.id)
      continue
    }
    chapterIdBySectionId.set(sec.id, chapterId)
    contentSections.push(sec)
  }

  // A content section is excluded from AI analysis either directly or via its parent chapter
  const isIncluded = (sec: Section) =>
    !sec.isExcludedFromAi && !excludedChapterIds.has(chapterIdBySectionId.get(sec.id) ?? NO_CHAPTER_ID)

  switch (scope) {
    case AiTeammateScope.EntirePlaybook:
      return contentSections.filter(isIncluded)

    case AiTeammateScope.CurrentSection: {
      // Separators and unknown sections are never a valid target
      const section = contentSections.find(sec => sec.id === currentSectionId)
      return section && isIncluded(section) ? [section] : []
    }

    case AiTeammateScope.CurrentChapter:
    default: {
      // Separators and unknown sections do not belong to any chapter scope
      if (currentSectionId === undefined || !chapterIdBySectionId.has(currentSectionId)) return []
      const currentChapterId = chapterIdBySectionId.get(currentSectionId)
      return contentSections.filter(sec => chapterIdBySectionId.get(sec.id) === currentChapterId && isIncluded(sec))
    }
  }
}
